/**
 * PUBLIC COLLECTION BROWSER
 * 
 * Allows visitors to browse a user's public collection, wishlist, or destash
 * 
 * Routes:
 *   /profile/:username/collection - Full collection with filters
 *   /profile/:username/wishlist - PublicWishlist items
 *   /profile/:username/destash - For sale/trade items
 * 
 * Respects user privacy settings
 * Glassmorphic styling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { Search, ArrowLeft, Grid, List, Heart, Loader2 } from 'lucide-react';
import type { CollectionItem } from '../types';
import type { AttachedItem } from '../messaging/types';
import InquiryModal from '../messaging/components/InquiryModal';
import PublicItemDetailModal from '../components/PublicItemDetailModal';
import PageVisibilityBanner from '../components/PageVisibilityBanner';
import { AppIcons } from '../constants/icons';

// View types
type ViewType = 'collection' | 'wishlist' | 'destash';

// Item type filters for collection view
type ItemTypeFilter = 'all' | 'polish' | 'plate' | 'tool' | 'embellishment' | 'other';

interface UserProfile {
  username: string;
  displayName: string;
  avatarUrl?: string;
  privacy?: {
    isPublic: boolean;
    showCollection: boolean;
    showWishlist: boolean;
    showDestashList: boolean;
    showFavorites: boolean;
  };
  isCollectionPublic?: boolean;
}

export default function PublicCollection() {
  const { username } = useParams<{ username: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read filter from URL query params
  const filterParam = searchParams.get('filter');
  const sortParam = searchParams.get('sort');
  const itemParam = searchParams.get('item');
  const photoParam = searchParams.get('photo');
  
  // Parse view from URL path (more reliable than useParams for nested routes)
  const getViewFromPath = (): ViewType => {
    const path = location.pathname.toLowerCase();
    if (path.endsWith('/wishlist')) return 'wishlist';
    if (path.endsWith('/destash')) return 'destash';
    return 'collection';
  };
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [items, setItems] = useState<CollectionItem[]>([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState<ItemTypeFilter>('all');
  const [sortBy, setSortBy] = useState<'name' | 'brand' | 'recent'>(sortParam === 'recent' ? 'recent' : 'name');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(filterParam === 'favorites');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  
  // View mode
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Selected item for detail view
  const [selectedItem, setSelectedItem] = useState<CollectionItem | null>(null);
  const [initialPhotoIndex, setInitialPhotoIndex] = useState(0);
  const isClosingRef = useRef(false); // Prevent re-opening during close

  // Inquiry modal state
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  // Current user for messaging
  const currentUser = getCurrentUser();
  
  // Favorite seller state
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Determine current view from URL path
  const currentView = getViewFromPath();

  // Load profile and items
  useEffect(() => {
    const loadData = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        // Look up user ID from username
        const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));
        
        if (!usernameDoc.exists()) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const foundUserId = usernameDoc.data()?.userId || usernameDoc.data()?.uid;
        if (!foundUserId) {
          setError('User not found');
          setLoading(false);
          return;
        }
        
        setUserId(foundUserId);

        // Check if this is the current user's profile
        const currentUser = getCurrentUser();
        const isOwn = currentUser?.uid === foundUserId;
        setIsOwnProfile(isOwn);

        // Load user profile
        const userDoc = await getDoc(doc(db, 'users', foundUserId));
        
        if (!userDoc.exists()) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        
        const profileData: UserProfile = {
          username: userData.profile?.username || userData.username || username,
          displayName: userData.profile?.displayName || userData.displayName || username,
          avatarUrl: userData.profile?.avatarUrl || userData.avatarUrl || '',
          privacy: userData.privacy,
          isCollectionPublic: userData.isCollectionPublic
        };

        setProfile(profileData);

        // Check privacy permissions
        const privacy = profileData.privacy;
        const isPublic = privacy?.isPublic || profileData.isCollectionPublic || false;
        
        if (!isPublic && !isOwn) {
          setError('This profile is private');
          setLoading(false);
          return;
        }

        // Check view-specific permissions
        if (!isOwn) {
          if (currentView === 'collection' && privacy?.showCollection === false) {
            setError('This collection is private');
            setLoading(false);
            return;
          }
          if (currentView === 'wishlist' && privacy?.showWishlist === false) {
            setError('This wishlist is private');
            setLoading(false);
            return;
          }
          if (currentView === 'destash' && privacy?.showDestashList === false) {
            setError('This destash list is private');
            setLoading(false);
            return;
          }
          // Check favorites filter permission
          if (filterParam === 'favorites' && privacy?.showFavorites === false) {
            setError('This user\'s favorites are private');
            setLoading(false);
            return;
          }
        }

        // Load items
        const itemsRef = collection(db, 'users', foundUserId, 'items');
        const itemsSnapshot = await getDocs(itemsRef);
        
        const loadedItems: CollectionItem[] = [];
        itemsSnapshot.forEach((doc) => {
          loadedItems.push({ id: doc.id, ...doc.data() } as CollectionItem);
        });
        
        setItems(loadedItems);
        setLoading(false);
      } catch (err) {
        console.error('Error loading collection:', err);
        setError('Error loading collection');
        setLoading(false);
      }
    };

    loadData();
  }, [username, location.pathname]);

  // Check if user has favorited this seller
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      const currentUser = getCurrentUser();
      if (!currentUser?.uid || !userId || currentUser.uid === userId) return;
      
      try {
        const favoriteDoc = await getDoc(
          doc(db, 'users', currentUser.uid, 'favoriteSellers', userId)
        );
        setIsFavorited(favoriteDoc.exists());
      } catch (err) {
        console.error('Error checking favorite status:', err);
      }
    };
    
    checkFavoriteStatus();
  }, [userId]);

  // Open item from URL parameter (for direct links/DMCA reports)
  useEffect(() => {
    // Skip if we're in the process of closing (prevents race condition)
    if (isClosingRef.current) return;

    if (itemParam && items.length > 0 && !selectedItem) {
      const itemToOpen = items.find(i => i.id === itemParam);
      if (itemToOpen) {
        setSelectedItem(itemToOpen);
        setInitialPhotoIndex(photoParam ? parseInt(photoParam, 10) || 0 : 0);
      }
    }
  }, [itemParam, photoParam, items, selectedItem]);

  // Helper to update URL when selecting/deselecting items
  const updateItemInUrl = (itemId: string | null, photoIndex?: number) => {
    const newParams = new URLSearchParams(searchParams);
    if (itemId) {
      newParams.set('item', itemId);
      if (photoIndex !== undefined && photoIndex > 0) {
        newParams.set('photo', photoIndex.toString());
      } else {
        newParams.delete('photo');
      }
    } else {
      newParams.delete('item');
      newParams.delete('photo');
    }
    setSearchParams(newParams, { replace: true });
  };

  // Handle selecting an item (updates URL)
  const handleSelectItem = (item: CollectionItem) => {
    setSelectedItem(item);
    setInitialPhotoIndex(0);
    updateItemInUrl(item.id, 0);
  };

  // Handle closing the modal (clears URL)
  const handleCloseModal = () => {
    // Set flag to prevent useEffect from re-opening during the close cycle
    isClosingRef.current = true;
    setSelectedItem(null);
    setInitialPhotoIndex(0);
    updateItemInUrl(null);
    // Reset the flag after a short delay to allow URL to update
    setTimeout(() => {
      isClosingRef.current = false;
    }, 100);
  };

  // Handle photo change in modal (updates URL) - wrapped in useCallback to prevent infinite loops
  const handlePhotoChange = useCallback((photoIndex: number) => {
    if (selectedItem) {
      updateItemInUrl(selectedItem.id, photoIndex);
    }
  }, [selectedItem, searchParams]);

  // Toggle favorite seller
  const toggleFavorite = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser?.uid || !userId || !profile) return;
    
    setFavoriteLoading(true);
    try {
      const favoriteRef = doc(db, 'users', currentUser.uid, 'favoriteSellers', userId);
      
      if (isFavorited) {
        await deleteDoc(favoriteRef);
        setIsFavorited(false);
      } else {
        await setDoc(favoriteRef, {
          sellerId: userId,
          sellerUsername: profile.username,
          sellerAvatar: profile.avatarUrl || null,
          addedAt: serverTimestamp()
        });
        setIsFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Filter items based on current view and filters
  const getFilteredItems = (): CollectionItem[] => {
    // Hide items the owner marked as private from every viewer other than
    // the owner themselves. The owner always sees their own private items
    // (the lock badge is applied by the tile — this filter just gates
    // visibility for outside viewers). Client-side only so we don't need a
    // new composite index — see also the mirroring filters on brand pages,
    // community item aggregation, and Discovery cross-user queries.
    let filtered = items.filter(item => isOwnProfile || item.isPrivate !== true);
    // Filter out deleted and already-destashed items
    filtered = filtered.filter(item => !item.isDestashed && !item.isDeleted);

    // Filter by view type
    if (currentView === 'wishlist') {
      filtered = filtered.filter(item => item.onWishlist);
    } else if (currentView === 'destash') {
      filtered = filtered.filter(item => item.isForDestash);
    }

    // Filter by favorites if enabled
    if (showFavoritesOnly) {
      filtered = filtered.filter(item => item.isFavorite);
    }

    // Filter by item type (collection view only)
    if (currentView === 'collection' && itemTypeFilter !== 'all') {
      if (itemTypeFilter === 'other') {
        filtered = filtered.filter(item => 
          !['polish', 'plate', 'tool', 'embellishment'].includes(item.itemType || '')
        );
      } else {
        filtered = filtered.filter(item => item.itemType === itemTypeFilter);
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(query) ||
        item.brand?.toLowerCase().includes(query) ||
        item.colorDescription?.toLowerCase().includes(query) ||
        item.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      } else if (sortBy === 'brand') {
        return (a.brand || '').localeCompare(b.brand || '');
      } else {
        // Recent - by createdAt
        const dateA = (a.createdAt as any)?.toDate?.() || new Date(0);
        const dateB = (b.createdAt as any)?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      }
    });

    return filtered;
  };

  const filteredItems = getFilteredItems();
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemTypeFilter, sortBy, currentView]);
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);
  
  // Generate page numbers to display
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Get item display image
  // Helper to check if a photo is hidden due to DMCA
  const isPhotoHidden = (photo: any): boolean => {
    if (typeof photo === 'object' && photo.dmcaStatus === 'hidden') {
      return true;
    }
    return false;
  };

  // Get first visible (non-DMCA-hidden) image for an item
  const getItemImage = (item: CollectionItem): string | null => {
    if (item.photos && item.photos.length > 0) {
      // First try to find a visible default photo
      const defaultPhoto = item.photos.find(p => p.isDefault && !isPhotoHidden(p));
      if (defaultPhoto) return defaultPhoto.url;
      // Otherwise find first visible photo
      const visiblePhoto = item.photos.find(p => !isPhotoHidden(p));
      if (visiblePhoto) return visiblePhoto.url;
    }
    if (item.swatchPhotos && item.swatchPhotos.length > 0) {
      const visible = (item.swatchPhotos as any[]).find(p => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (item.bottlePhotos && item.bottlePhotos.length > 0) {
      const visible = (item.bottlePhotos as any[]).find(p => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (item.inUsePhotos && item.inUsePhotos.length > 0) {
      const visible = (item.inUsePhotos as any[]).find(p => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (item.macroPhotos && item.macroPhotos.length > 0) {
      const visible = (item.macroPhotos as any[]).find(p => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    return null;
  };

  // Get item type icon
  const getItemTypeIcon = (itemType?: string) => {
    switch (itemType) {
      case 'polish': return AppIcons.Polish;
      case 'plate': return AppIcons.Plate;
      case 'tool': return AppIcons.Tool;
      case 'embellishment': return AppIcons.Embellishment;
      default: return AppIcons.Explore;
    }
  };

  // Get view title and icon
  const getViewInfo = () => {
    // If showing favorites, override the title
    if (showFavoritesOnly) {
      return { title: 'Favorites', Icon: Heart };
    }
    
    switch (currentView) {
      case 'wishlist':
        return { title: 'Wishlist', Icon: AppIcons.Wishlist };
      case 'destash':
        return { title: 'For Sale / Trade', Icon: AppIcons.Tags };
      default:
        return { title: 'Collection', Icon: AppIcons.Explore };
    }
  };

  const viewInfo = getViewInfo();

  // Count by item type (for collection view)
  const itemTypeCounts = {
    all: items.filter(i => !i.isDestashed && !i.isDeleted && (currentView === 'collection' ? true :
         currentView === 'wishlist' ? i.onWishlist : i.isForDestash)).length,
    polish: items.filter(i => !i.isDestashed && !i.isDeleted && i.itemType === 'polish').length,
    plate: items.filter(i => !i.isDestashed && !i.isDeleted && i.itemType === 'plate').length,
    tool: items.filter(i => !i.isDestashed && !i.isDeleted && i.itemType === 'tool').length,
    embellishment: items.filter(i => !i.isDestashed && !i.isDeleted && i.itemType === 'embellishment').length,
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 border-4 border-slate-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-glass-secondary font-body">Loading collection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="glass-card p-8 max-w-md mx-auto text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-glass mb-2 font-heading">
            {error}
          </h2>
          <p className="text-glass-secondary mb-6 font-body">
            {error.includes('private') 
              ? "This user has chosen to keep this content private."
              : "We couldn't find what you're looking for."}
          </p>
          <Link
            to={`/profile/${username}`}
            className="inline-block px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default font-body"
          >
            View Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      {/* Visibility Banner - Only for profile owner */}
      {isOwnProfile && profile?.username && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <PageVisibilityBanner 
            isPublic={
              currentView === 'collection' ? profile?.privacy?.showCollection !== false :
              currentView === 'wishlist' ? profile?.privacy?.showWishlist !== false :
              profile?.privacy?.showDestashList !== false
            }
            publicPath={`/profile/${profile.username}/${currentView}`}
            pageType={currentView as 'collection' | 'wishlist' | 'destash'}
            isPreviewMode={true}
            onPreview={() => navigate('/collection')}
          />
        </div>
      )}

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Back to Profile & Main Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to={`/profile/${username}`}
            className="p-2 hover:bg-slate-800/60 rounded-lg transition-default"
          >
            <ArrowLeft className="icon-base text-glass-secondary" />
          </Link>

          <div className="flex items-center gap-4 flex-1">
            {profile?.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt={profile.displayName}
                className="w-14 h-14 rounded-full object-cover border-2 border-slate-600"
              />
            ) : (
              <div className="w-14 h-14 rounded-full rainbow-gradient flex items-center justify-center text-white text-xl font-bold">
                {(profile?.displayName || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-glass font-heading">
                {profile?.displayName}'s {viewInfo.title}
              </h1>
              <p className="text-glass-secondary font-body">
                {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
              </p>
            </div>
          </div>

          {/* Follow Button - Only show for other users */}
          {!isOwnProfile && userId && (
            <button
              onClick={toggleFavorite}
              disabled={favoriteLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all font-body ${
                isFavorited
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg'
                  : 'bg-slate-800/60 hover:bg-slate-700/60 text-glass-secondary border border-slate-600'
              }`}
              title={isFavorited ? 'Unfollow this seller' : 'Follow this seller'}
            >
              {favoriteLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
              )}
              <span className="hidden sm:inline">
                {isFavorited ? 'Following' : 'Follow'}
              </span>
            </button>
          )}
        </div>

        {/* View Tabs */}
        <div className="glass-card p-1 inline-flex gap-1">
          <Link
            to={`/profile/${username}/collection`}
            className={`px-4 py-2 rounded-lg font-medium transition-default font-body flex items-center gap-2 ${
              currentView === 'collection'
                ? 'bg-slate-700 text-white'
                : 'text-glass-secondary hover:bg-slate-800/40'
            }`}
          >
            <AppIcons.Explore className="icon-sm" /> Collection
          </Link>
          <Link
            to={`/profile/${username}/wishlist`}
            className={`px-4 py-2 rounded-lg font-medium transition-default font-body flex items-center gap-2 ${
              currentView === 'wishlist'
                ? 'bg-slate-700 text-white'
                : 'text-glass-secondary hover:bg-slate-800/40'
            }`}
          >
            <AppIcons.Wishlist className="icon-sm" /> Wishlist
          </Link>
          <Link
            to={`/profile/${username}/destash`}
            className={`px-4 py-2 rounded-lg font-medium transition-default font-body flex items-center gap-2 ${
              currentView === 'destash'
                ? 'bg-slate-700 text-white'
                : 'text-glass-secondary hover:bg-slate-800/40'
            }`}
          >
            <AppIcons.Tags className="icon-sm" /> Destash
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-grow relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${viewInfo.title.toLowerCase()}...`}
                className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-white/80 font-body"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-white/80 font-body"
            >
              <option value="name">Sort by Name</option>
              <option value="brand">Sort by Brand</option>
              <option value="recent">Sort by Recent</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex gap-1 bg-slate-800/60 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-default ${
                  viewMode === 'grid' ? 'bg-white shadow text-glass-secondary' : 'text-glass-muted hover:text-glass-secondary'
                }`}
              >
                <Grid className="icon-md" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-default ${
                  viewMode === 'list' ? 'bg-white shadow text-glass-secondary' : 'text-glass-muted hover:text-glass-secondary'
                }`}
              >
                <List className="icon-md" />
              </button>
            </div>
          </div>

          {/* Item Type Filters (collection view only) */}
          {currentView === 'collection' && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-600">
              {[
                { id: 'all', label: 'All', Icon: AppIcons.Explore },
                { id: 'polish', label: 'Polishes', Icon: AppIcons.Polish, count: itemTypeCounts.polish },
                { id: 'plate', label: 'Plates', Icon: AppIcons.Plate, count: itemTypeCounts.plate },
                { id: 'tool', label: 'Tools', Icon: AppIcons.Tool, count: itemTypeCounts.tool },
                { id: 'embellishment', label: 'Embellishments', Icon: AppIcons.Embellishment, count: itemTypeCounts.embellishment },
              ].map((type) => {
                const IconComponent = type.Icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setItemTypeFilter(type.id as ItemTypeFilter)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-default font-body flex items-center gap-1.5 ${
                      itemTypeFilter === type.id
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                    }`}
                  >
                    <IconComponent className="icon-xs" /> {type.label}
                    {type.count !== undefined && (
                      <span className="ml-1 opacity-70">({type.count})</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Favorites Filter Indicator */}
          {showFavoritesOnly && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-600">
              <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-pink-500/20 text-pink-300 border border-pink-500/50 flex items-center gap-1.5">
                <Heart className="icon-xs fill-current" /> Showing Favorites Only
              </span>
              <button
                onClick={() => {
                  setShowFavoritesOnly(false);
                  navigate(`/profile/${username}/collection`);
                }}
                className="px-3 py-1.5 rounded-full text-sm font-medium bg-slate-800/60 text-glass-secondary hover:bg-white/80 transition-default"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>

        {/* Items Grid/List */}
        {filteredItems.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {paginatedItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelectItem(item)}
                  className="glass-card-sm overflow-hidden cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group"
                >
                  {/* Image */}
                  <div className="aspect-square bg-gradient-to-br from-fuchsia-100 to-purple-100 relative overflow-hidden">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {(() => {
                          const IconComponent = getItemTypeIcon(item.itemType);
                          return <IconComponent className="icon-2xl text-glass-secondary" />;
                        })()}
                      </div>
                    )}

                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      {item.isFavorite && (
                        <span className="p-1.5 bg-pink-500 text-white rounded-full">
                          <AppIcons.Favorite className="icon-xs" />
                        </span>
                      )}
                      {item.isForDestash && (
                        <span className="p-1.5 bg-green-500 text-white rounded-full">
                          <AppIcons.Tags className="icon-xs" />
                        </span>
                      )}
                    </div>

                    {/* Price for destash items */}
                    {currentView === 'destash' && item.destashPrice && (
                      <div className="absolute bottom-2 right-2">
                        <span className="px-2 py-1 bg-green-500 text-white text-sm font-bold rounded-lg">
                          ${item.destashPrice}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-semibold text-glass truncate text-sm font-body">
                      {item.name}
                    </h3>
                    <p className="text-xs text-glass-muted truncate">{item.brand}</p>
                    {item.colorDescription && (
                      <p className="text-xs text-glass-muted truncate mt-0.5">{item.colorDescription}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="glass-card overflow-hidden">
              {paginatedItems.map((item, idx) => (
                <div
                  key={item.id}
                  onClick={() => handleSelectItem(item)}
                  className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-700/50 transition-default ${
                    idx > 0 ? 'border-t border-slate-600' : ''
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-fuchsia-100 to-purple-100 flex-shrink-0 overflow-hidden">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {(() => {
                          const IconComponent = getItemTypeIcon(item.itemType);
                          return <IconComponent className="icon-lg text-glass-secondary" />;
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-grow min-w-0">
                    <h3 className="font-semibold text-glass truncate font-body">
                      {item.name}
                    </h3>
                    <p className="text-sm text-glass-muted">{item.brand}</p>
                    {item.colorDescription && (
                      <p className="text-sm text-glass-muted truncate">{item.colorDescription}</p>
                    )}
                  </div>

                  {/* Badges & Price */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.isFavorite && (
                      <AppIcons.Favorite className="icon-sm text-pink-500 fill-current" />
                    )}
                    {currentView === 'destash' && item.destashPrice && (
                      <span className="px-2 py-1 bg-green-500 text-white text-sm font-bold rounded-lg">
                        ${item.destashPrice}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="glass-card p-12 text-center">
            <viewInfo.Icon className="w-16 h-16 text-glass-secondary mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2 font-heading text-glass">
              {searchQuery ? 'No Matches Found' : `No Items in ${viewInfo.title}`}
            </h3>
            <p className="text-glass-secondary font-body">
              {searchQuery
                ? 'Try adjusting your search or filters.'
                : `${profile?.displayName} hasn't added anything here yet.`}
            </p>
          </div>
        )}

        {/* Pagination */}
        {filteredItems.length > itemsPerPage && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 glass-card p-4">
            {/* Results info */}
            <p className="text-sm text-glass-secondary font-body">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredItems.length)} of {filteredItems.length} items
            </p>
            
            {/* Page controls */}
            <div className="flex items-center gap-1">
              {/* Back button */}
              <button
                onClick={() => {
                  setCurrentPage(prev => Math.max(1, prev - 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-lg font-medium transition-default font-body ${
                  currentPage === 1
                    ? 'bg-white/40 text-glass-muted cursor-not-allowed'
                    : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                }`}
              >
                ← Back
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1 mx-2">
                {getPageNumbers().map((page, idx) => (
                  typeof page === 'number' ? (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentPage(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-10 h-10 rounded-lg font-medium transition-default font-body ${
                        currentPage === page
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                      }`}
                    >
                      {page}
                    </button>
                  ) : (
                    <span key={idx} className="px-2 text-glass-muted">...</span>
                  )
                ))}
              </div>

              {/* Next button */}
              <button
                onClick={() => {
                  setCurrentPage(prev => Math.min(totalPages, prev + 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 rounded-lg font-medium transition-default font-body ${
                  currentPage === totalPages
                    ? 'bg-white/40 text-glass-muted cursor-not-allowed'
                    : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Item Detail Modal */}
      {selectedItem && userId && profile && (
        <PublicItemDetailModal
          item={selectedItem}
          onClose={handleCloseModal}
          ownerUsername={profile.username}
          ownerDisplayName={profile.displayName || profile.username}
          ownerId={userId}
          isOwnProfile={isOwnProfile}
          onInquire={() => setShowInquiryModal(true)}
          initialPhotoIndex={initialPhotoIndex}
          onPhotoChange={handlePhotoChange}
        />
      )}

      {/* In-App Messaging Inquiry Modal */}
      {currentUser && (
        <InquiryModal
          isOpen={showInquiryModal && !!selectedItem && !!userId}
          onClose={() => setShowInquiryModal(false)}
          currentUserId={currentUser.uid}
          sellerId={userId || ''}
          sellerUsername={profile?.username || null}
          items={selectedItem ? [{
            itemId: selectedItem.id,
            ownerId: userId || '',
            name: selectedItem.name,
            brand: selectedItem.brand,
            photoUrl: selectedItem.photos?.[0]?.url || null
          } as AttachedItem] : []}
        />
      )}
    </div>
  );
}
